import {
  Alert,
  Button,
  Col,
  DesignTokens,
  FullWidthSpace,
  LabeledItem,
  Row,
  Space,
  Table,
  Text,
  Title,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useFormikContext } from "formik";
import { DisplayFormErrors, DrawerFooterContent } from "melco-shared-logic";
import dayjs from "dayjs";
import { useState } from "react";
import { formatUTCDate } from "../../../../helper/dateHelper";

const Right = styled(Space)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const { spacings } = DesignTokens;

type DisplayChangeLevelLicenseSummaryProps = {
  stepback: () => void;
  newValuesToChangeLevel: any;
  currentLicense: any;
};



export const DisplayChangeLevelLicenseSummary: React.FC<
  DisplayChangeLevelLicenseSummaryProps
> = ({ stepback, currentLicense, newValuesToChangeLevel }) => {
  const { t } = useTranslation();
  const { submitForm } = useFormikContext();
  const [productIsSubscription] = useState(
    currentLicense?.is_subscription ?? false
  );
  const columns = [
    {
      title: "",
      dataIndex: "field",
      width: 300,
      // render: (value: string) => <Text type="secondary"></Text>,
    },
    {
      title: t("licensemanager.changelevel.table.columns.current"),
      dataIndex: "current",
      //width: 120,
      render: (value: string) => <Text >{value}</Text>,
    },
    {
      title: t("licensemanager.changelevel.table.columns.new"),
      dataIndex: "new",
    },
  ];

  /**
  * Subscription Date Display Logic — Developer Notes:
  *
  * 1. We show the "current" date in the UI for review purposes.
  *    - Use `renewal_date` if it exists and is valid.
  *    - Otherwise, use `subscription_start_date`.
  *    - Assumption: `subscription_start_date` is always required and valid.
  *
  * 2. We show the "new" selected value from the form.
  *    - Only format and display it if it's a valid date.
  *
  * 3. Dates are formatted as MM/DD/YYYY for consistency across the UI.
  */
  const data = productIsSubscription
    ? [
      {
        key: "startdate",
        current: currentLicense.renewal_date
          ? dayjs(currentLicense.renewal_date).isValid()
            ? formatUTCDate(currentLicense.renewal_date, "MM/DD/YYYY")
            : null
          : dayjs(currentLicense.subscription_start_date).isValid()
            ? formatUTCDate(currentLicense.subscription_start_date, "MM/DD/YYYY")
            : null,
        new: dayjs(newValuesToChangeLevel.subscription_start_date).isValid()
          ? dayjs(newValuesToChangeLevel.subscription_start_date).format("MM/DD/YYYY")
          : null,
        field: currentLicense.renewal_date
          ? t("licensemanager.changelevel.form.renewes_on")
          : t("licensemanager.changelevel.form.subscription_start_date"),
      },
      {
        key: "level",
        field: t("licensemanager.changelevel.form.level"),
        current: currentLicense.product_level_name,
        new: newValuesToChangeLevel.new_subscription_code_display_value,
      },
      {
        key: "totalcost",
        current: (
          <strong>
            {"$" +
              (currentLicense.subscription_cost
                ? currentLicense.subscription_cost
                : "0") + " / " +
              (currentLicense.product_level_code
                ? currentLicense.product_level_code.toLowerCase().replace("ly", "")
                : "")}
          </strong>
        ),
        new: (
          <strong>
            {"$" +
              (currentLicense.new_subscription_cost
                ? currentLicense.new_subscription_cost
                : "0") +
              " / " +
              (newValuesToChangeLevel.new_subscription_code
                ? newValuesToChangeLevel.new_subscription_code.toLowerCase().replace("ly", "")
                : "")}
          </strong>
        ),
        field: <strong>{t('licensemanager.changelevel.table.columns.total_cost')}</strong>,
      },
    ]
    : [
      {
        key: "level",
        field: t("licensemanager.changelevel.form.level"),
        current: currentLicense.product_level_name,
        new: newValuesToChangeLevel.new_subscription_code_display_value,
      },
    ];

  return (
    <>
      <div>
        <FullWidthSpace direction="vertical" size="small">
          <Title level={4}>
            {t("licensemanager.changelevel.form.summary")}
          </Title>
          <DisplayFormErrors />
          <Alert
            type="info"
            withBackground
            message={t("licensemanager.changelevel.alert.message")}
          />
          <div>
            <Title level={4}>
              {t("licensemanager.changelevel.form.licensedetails")}
            </Title>

            <Row gutter={[spacings.middle, 0]}>
              <Col xs={24} md={8}>
                <Space direction="vertical" size="middle">
                  <LabeledItem
                    label={t(
                      "licensemanager.changelevel.form.melco_customer_id"
                    )}
                    content={currentLicense.melco_customer_id}
                    fallback={t("licensemanager.changelevel.form.fallback")}
                  />
                  <LabeledItem
                    label={t("licensemanager.changelevel.form.product")}
                    content={currentLicense.product_name}
                    fallback={t("licensemanager.changelevel.form.fallback")}
                  />

                  <LabeledItem
                    label={t("licensemanager.changelevel.form.version")}
                    content={currentLicense.product_version_number}
                    fallback={t("licensemanager.changelevel.form.fallback")}
                  />

                  {/* <LabeledItem
                    label={t("licensemanager.changelevel.form.level")}
                    content={currentLicense.product_level_name}
                    fallback={t("licensemanager.changelevel.form.fallback")}
                  /> */}
                </Space>
              </Col>
              <Col xs={24} md={8}>
                <Space direction="vertical" size="middle">
                  <LabeledItem
                    label={t("licensemanager.changelevel.form.owner")}
                    content={
                      <>
                        <Row>{currentLicense.master_user_name}</Row>
                        <Row>{currentLicense.master_name}</Row>
                      </>
                    }
                    fallback={t("licensemanager.changelevel.form.fallback")}
                  />
                  <LabeledItem
                    label={t("licensemanager.changelevel.form.assigneduser")}
                    content={
                      <>
                        <Row>{currentLicense.assigned_user_name}</Row>
                        <Row>{currentLicense.assigned_name}</Row>
                      </>
                    }
                    fallback={t("licensemanager.changelevel.form.fallback")}
                  />
                  <LabeledItem
                    label={t("licensemanager.changelevel.form.level")}
                    content={currentLicense.product_level_name}
                    fallback={t("licensemanager.changelevel.form.fallback")}
                  />
                  {productIsSubscription && (
                    <>
                      <LabeledItem
                        label={t("licensemanager.changelevel.form.subscription_start_date")}
                        content={
                          dayjs(
                            currentLicense?.subscription_start_date ?? ""
                          ).isValid()
                            ? formatUTCDate(
                              currentLicense?.subscription_start_date
                              , "MM/DD/YYYY")
                            : ""
                        }
                        fallback={t("global.fallback.empty")}
                      />
                      <LabeledItem
                        label={t("licensemanager.changelevel.form.renewes_on")}
                        content={
                          dayjs(currentLicense?.renewal_date ?? "").isValid()
                            ? formatUTCDate(currentLicense?.renewal_date, "MM/DD/YYYY")
                            : ""
                        }
                        fallback={t("global.fallback.empty")}
                      />
                    </>
                  )}
                </Space>
              </Col>

              <Col xs={24} md={8}>
                <Space direction="vertical" size="middle">
                  <LabeledItem
                    label={t("licensemanager.changelevel.form.serial_number")}
                    content={currentLicense.serial_number}
                    fallback={t("licensemanager.changelevel.form.fallback")}
                  />
                  <LabeledItem
                    label={t("licensemanager.changelevel.form.language")}
                    content={
                      currentLicense.language && currentLicense.language !== ""
                        ? t(`language.${currentLicense.language}` as any)
                        : ""
                    }
                    fallback={t("licensemanager.changelevel.form.fallback")}
                  />
                </Space>
              </Col>
            </Row>
          </div>
          <div>
            <Title level={4}>
              {t("licensemanager.changelevel.form.title.changes")}
            </Title>

            <Table
              columns={columns}
              dataSource={data}
              bordered
              pagination={false}
            />
          </div>
        </FullWidthSpace>
        <DrawerFooterContent>
          <Row>
            <Col xs={24} md={12}>
              <Button
                style={{ minWidth: "80px" }}
                key={"back-btn"}
                type="primary"
                onClick={stepback}
              >
                {t("licensemanager.buttons.back")}
              </Button>
            </Col>
            <Col
              style={{ display: "flex", justifyContent: "flex-end" }}
              xs={24}
              md={12}
            >
              <Right>
                <Button
                  style={{ minWidth: "80px" }}
                  key="add-user-button"
                  type="primary"
                  onClick={() => submitForm()}
                >
                  {t("licensemanager.buttons.changelicense&sendemail")}
                </Button>
              </Right>
            </Col>
          </Row>
        </DrawerFooterContent>
      </div>
    </>
  );
};
